/**
 * Phase 2: Enhanced Features - Frontend Verification Error Constants
 * 
 * Mirror of backend error codes and messages for consistent error handling
 * across frontend and backend systems.
 */

// Error codes (identical to backend)
export const VERIFICATION_ERROR_CODES = {
  // Input Validation Errors
  INVALID_GHANA_CARD_FORMAT: 'INVALID_GHANA_CARD_FORMAT',
  INVALID_IMAGE_FORMAT: 'INVALID_IMAGE_FORMAT',
  IMAGE_FILE_TOO_LARGE: 'IMAGE_FILE_TOO_LARGE',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  
  // NIA Service Errors
  NIA_SERVICE_UNAVAILABLE: 'NIA_SERVICE_UNAVAILABLE',
  NIA_VERIFICATION_FAILED: 'NIA_VERIFICATION_FAILED',
  NIA_INVALID_RESPONSE: 'NIA_INVALID_RESPONSE',
  NIA_RATE_LIMIT_EXCEEDED: 'NIA_RATE_LIMIT_EXCEEDED',
  
  // Duplicate and Reuse Errors
  VERIFICATION_ALREADY_EXISTS: 'VERIFICATION_ALREADY_EXISTS',
  VERIFICATION_ALREADY_LINKED: 'VERIFICATION_ALREADY_LINKED',
  VERIFICATION_NOT_REUSABLE: 'VERIFICATION_NOT_REUSABLE',
  
  // Access and Permission Errors
  ACCESS_DENIED: 'ACCESS_DENIED',
  VERIFICATION_NOT_FOUND: 'VERIFICATION_NOT_FOUND',
  CLAIM_NOT_FOUND: 'CLAIM_NOT_FOUND',
  VERIFICATION_NOT_VERIFIED: 'VERIFICATION_NOT_VERIFIED',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  
  // Database and Internal Errors
  DATABASE_ERROR: 'DATABASE_ERROR',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  STORAGE_SERVICE_ERROR: 'STORAGE_SERVICE_ERROR',
  
  // Network and External Service Errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVICE_TIMEOUT: 'SERVICE_TIMEOUT',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR'
};

// **PHASE 2: TASK 2 - COMPREHENSIVE NIA ERROR CODE MAPPINGS**
// Official NIA API error codes and their user-friendly translations
export const NIA_ERROR_MAPPINGS = {
  '00': 'Ghana Card verification successful',
  '01': 'Ghana Card verification failed. Please check your details and try again',
  '11': 'Face detection failed. Please retake photo in better lighting conditions',
  '20': 'Invalid image format. Please upload a clear photo of your Ghana Card',
  '21': 'Invalid request format. Please try again',
  '22': 'Missing required information. Please fill in all fields',
  '23': 'Invalid image format. Please upload a PNG or JPEG image',
  '24': 'Image file too large. Please upload a smaller image (max 5MB)',
  '25': 'Image file too small. Please upload a clearer image',
  '26': 'Face not detected in image. Please ensure your face is clearly visible',
  '27': 'Multiple faces detected. Please ensure only one person is in the image',
  '28': 'Poor image quality. Please retake photo in better lighting',
  '99': 'System error occurred. Please contact support if this persists'
};

// User-friendly error messages (identical to backend)
export const VERIFICATION_ERROR_MESSAGES = {
  // Input Validation Errors
  [VERIFICATION_ERROR_CODES.INVALID_GHANA_CARD_FORMAT]: 'Please enter a valid Ghana Card number in the format GHA-XXXXXXXXX-X',
  [VERIFICATION_ERROR_CODES.INVALID_IMAGE_FORMAT]: 'Please upload a valid image file (JPEG, PNG, or WebP)',
  [VERIFICATION_ERROR_CODES.IMAGE_FILE_TOO_LARGE]: 'Image file is too large. Please upload an image smaller than 5MB',
  [VERIFICATION_ERROR_CODES.MISSING_REQUIRED_FIELD]: 'Please fill in all required fields to continue',
  
  // NIA Service Errors
  [VERIFICATION_ERROR_CODES.NIA_SERVICE_UNAVAILABLE]: 'Verification service is temporarily unavailable. Please try again later',
  [VERIFICATION_ERROR_CODES.NIA_VERIFICATION_FAILED]: 'Ghana Card verification failed. Please check your details and try again',
  [VERIFICATION_ERROR_CODES.NIA_INVALID_RESPONSE]: 'Invalid response from verification service. Please try again',
  [VERIFICATION_ERROR_CODES.NIA_RATE_LIMIT_EXCEEDED]: 'Too many verification attempts. Please wait before trying again',
  
  // Duplicate and Reuse Errors
  [VERIFICATION_ERROR_CODES.VERIFICATION_ALREADY_EXISTS]: 'This Ghana Card has already been verified for this claim',
  [VERIFICATION_ERROR_CODES.VERIFICATION_ALREADY_LINKED]: 'This verification is already linked to another claim',
  [VERIFICATION_ERROR_CODES.VERIFICATION_NOT_REUSABLE]: 'This verification cannot be reused for the current request',
  
  // Access and Permission Errors
  [VERIFICATION_ERROR_CODES.ACCESS_DENIED]: 'You do not have permission to access this verification',
  [VERIFICATION_ERROR_CODES.VERIFICATION_NOT_FOUND]: 'Verification record not found',
  [VERIFICATION_ERROR_CODES.CLAIM_NOT_FOUND]: 'Claim not found',
  [VERIFICATION_ERROR_CODES.VERIFICATION_NOT_VERIFIED]: 'Ghana Card has not been successfully verified',
  [VERIFICATION_ERROR_CODES.INSUFFICIENT_PERMISSIONS]: 'Insufficient permissions to perform this action',
  
  // Database and Internal Errors
  [VERIFICATION_ERROR_CODES.DATABASE_ERROR]: 'A database error occurred. Please try again later',
  [VERIFICATION_ERROR_CODES.INTERNAL_SERVER_ERROR]: 'An internal server error occurred. Please contact support if the problem persists',
  [VERIFICATION_ERROR_CODES.STORAGE_SERVICE_ERROR]: 'File storage service is unavailable. Please try again later',
  
  // Network and External Service Errors
  [VERIFICATION_ERROR_CODES.NETWORK_ERROR]: 'Network error occurred. Please check your internet connection and try again',
  [VERIFICATION_ERROR_CODES.SERVICE_TIMEOUT]: 'Service request timed out. Please try again',
  [VERIFICATION_ERROR_CODES.EXTERNAL_SERVICE_ERROR]: 'External service error occurred. Please try again later'
};

/**
 * Helper function to get user-friendly error message by code
 * @param {string} code - Error code
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(code) {
  return VERIFICATION_ERROR_MESSAGES[code] || 'An unknown error occurred during verification';
}

/**
 * **PHASE 2: TASK 2 - NIA ERROR CODE TRANSLATION**
 * Get user-friendly message for NIA API error codes
 * @param {string} niaCode - NIA API error code (e.g., '01', '11', '26')
 * @returns {string} User-friendly error message
 */
export function getNiaErrorMessage(niaCode) {
  return NIA_ERROR_MAPPINGS[niaCode] || `Unknown verification error (Code: ${niaCode})`;
}

/**
 * **PHASE 3: COMPATIBILITY FUNCTION**
 * Translate NIA errors with additional context for error handler
 * @param {string} code - Error code (may be NIA code or general error code)
 * @param {string} message - Original error message
 * @returns {Object} Translation object with user-friendly details
 */
export function translateNIAError(code, message) {
  // Extract NIA code from various formats
  let niaCode = null;
  
  // Check if code is already a NIA code (2 digits)
  if (/^\d{2}$/.test(code)) {
    niaCode = code;
  }
  // Extract from message or details
  else if (message) {
    const niaCodeMatch = message.match(/code[:\s]*['"]?(\d{2})['"]?/i);
    if (niaCodeMatch) {
      niaCode = niaCodeMatch[1];
    }
  }
  
  // If we found a NIA code, translate it
  if (niaCode && NIA_ERROR_MAPPINGS[niaCode]) {
    const userMessage = NIA_ERROR_MAPPINGS[niaCode];
    let suggestion = 'Please try again.';
    
    // Provide specific suggestions based on error type
    switch (niaCode) {
      case '11':
      case '26':
      case '28':
        suggestion = 'Please take a new photo in better lighting with your face clearly visible.';
        break;
      case '20':
      case '23':
        suggestion = 'Please upload a clear JPEG or PNG image.';
        break;
      case '24':
        suggestion = 'Please upload a smaller image file (under 5MB).';
        break;
      case '25':
        suggestion = 'Please upload a clearer, higher quality image.';
        break;
      case '27':
        suggestion = 'Please ensure only one person is visible in the photo.';
        break;
      case '01':
        suggestion = 'Please check your Ghana Card number and try again.';
        break;
      case '22':
        suggestion = 'Please fill in all required fields.';
        break;
      default:
        suggestion = 'Please contact support if this problem persists.';
    }
    
    return {
      userFriendly: true,
      userMessage,
      suggestion,
      niaCode,
      originalCode: code
    };
  }
  
  // For non-NIA codes, provide basic translation
  if (code && VERIFICATION_ERROR_MESSAGES[code]) {
    return {
      userFriendly: true,
      userMessage: VERIFICATION_ERROR_MESSAGES[code],
      suggestion: 'Please try again or contact support if the problem persists.',
      niaCode: null,
      originalCode: code
    };
  }
  
  // Fallback for unknown errors
  return {
    userFriendly: false,
    userMessage: message || 'An unknown error occurred',
    suggestion: 'Please contact support for assistance.',
    niaCode: null,
    originalCode: code
  };
}

/**
 * Helper function to check if an error code exists
 * @param {string} code - Error code to check
 * @returns {boolean} True if code exists
 */
export function isValidErrorCode(code) {
  return Object.values(VERIFICATION_ERROR_CODES).includes(code);
}

/**
 * **PHASE 2: TASK 3 - ENHANCED BACKEND ERROR FORMAT PROCESSING**
 * Process backend error response in new standardized format: {error, details, code}
 * @param {Object} errorResponse - Backend error response
 * @returns {Object} Processed error information
 */
export function processBackendError(errorResponse) {
  const { error, details, code } = errorResponse || {};
  
  // If we have a standard error code, use it
  if (code && isValidErrorCode(code)) {
    return {
      message: error || getErrorMessage(code),
      code: code,
      details: details,
      isStandardError: true
    };
  }
  
  // Check if details contains NIA error code
  if (details && typeof details === 'string') {
    const niaCodeMatch = details.match(/code[:\s]*['"]?(\d{2})['"]?/i);
    if (niaCodeMatch) {
      const niaCode = niaCodeMatch[1];
      return {
        message: getNiaErrorMessage(niaCode),
        code: VERIFICATION_ERROR_CODES.NIA_VERIFICATION_FAILED,
        details: details,
        niaCode: niaCode,
        isNiaError: true
      };
    }
  }
  
  // Fallback to basic error message
  return {
    message: error || 'An error occurred during verification',
    code: VERIFICATION_ERROR_CODES.INTERNAL_SERVER_ERROR,
    details: details,
    isStandardError: false
  };
}

/**
 * Helper function to map HTTP status codes to appropriate error codes
 * @param {number} status - HTTP status code
 * @param {string} message - Original error message
 * @returns {string} Appropriate error code
 */
export function mapHttpStatusToErrorCode(status, message = '') {
  const lowerMessage = message.toLowerCase();
  
  switch (status) {
    case 400:
      if (lowerMessage.includes('ghana card')) return VERIFICATION_ERROR_CODES.INVALID_GHANA_CARD_FORMAT;
      if (lowerMessage.includes('image')) return VERIFICATION_ERROR_CODES.INVALID_IMAGE_FORMAT;
      return VERIFICATION_ERROR_CODES.MISSING_REQUIRED_FIELD;
      
    case 403:
      return VERIFICATION_ERROR_CODES.ACCESS_DENIED;
      
    case 404:
      if (lowerMessage.includes('verification')) return VERIFICATION_ERROR_CODES.VERIFICATION_NOT_FOUND;
      if (lowerMessage.includes('claim')) return VERIFICATION_ERROR_CODES.CLAIM_NOT_FOUND;
      return VERIFICATION_ERROR_CODES.VERIFICATION_NOT_FOUND;
      
    case 409:
      return VERIFICATION_ERROR_CODES.VERIFICATION_ALREADY_EXISTS;
      
    case 413:
      return VERIFICATION_ERROR_CODES.IMAGE_FILE_TOO_LARGE;
      
    case 422:
      return VERIFICATION_ERROR_CODES.NIA_VERIFICATION_FAILED;
      
    case 429:
      return VERIFICATION_ERROR_CODES.NIA_RATE_LIMIT_EXCEEDED;
      
    case 500:
      return VERIFICATION_ERROR_CODES.INTERNAL_SERVER_ERROR;
      
    case 502:
    case 503:
      return VERIFICATION_ERROR_CODES.NIA_SERVICE_UNAVAILABLE;
      
    case 504:
      return VERIFICATION_ERROR_CODES.SERVICE_TIMEOUT;
      
    default:
      if (status >= 500) return VERIFICATION_ERROR_CODES.INTERNAL_SERVER_ERROR;
      if (status >= 400) return VERIFICATION_ERROR_CODES.EXTERNAL_SERVICE_ERROR;
      return VERIFICATION_ERROR_CODES.NETWORK_ERROR;
  }
}

/**
 * **PHASE 2: TASK 3 - ENHANCED ERROR PROCESSING FOR NEW BACKEND FORMAT**
 * Enhanced error handling for verification responses from Phase 1 backend changes
 * @param {Error} error - The error object
 * @param {number} status - HTTP status code  
 * @returns {Object} Processed error information
 */
export function processVerificationError(error, status = null) {
  let code = VERIFICATION_ERROR_CODES.INTERNAL_SERVER_ERROR;
  let message = 'An unknown error occurred during verification';
  let details = null;
  let niaCode = null;
  
  // **PHASE 2: Process new backend error format {error, details, code}**
  if (error.response && error.response.data) {
    const backendError = processBackendError(error.response.data);
    return {
      code: backendError.code,
      message: backendError.message,
      details: backendError.details,
      niaCode: backendError.niaCode,
      originalError: error.message,
      status: status || error.response.status
    };
  }
  
  // Check if error already has a code
  if (error.code && isValidErrorCode(error.code)) {
    code = error.code;
    message = getErrorMessage(code);
  } else if (status) {
    // Map HTTP status to error code
    code = mapHttpStatusToErrorCode(status, error.message);
    message = getErrorMessage(code);
  } else if (error.message) {
    // Try to infer from error message
    const lowerMessage = error.message.toLowerCase();
    if (lowerMessage.includes('network')) {
      code = VERIFICATION_ERROR_CODES.NETWORK_ERROR;
    } else if (lowerMessage.includes('timeout')) {
      code = VERIFICATION_ERROR_CODES.SERVICE_TIMEOUT;
    } else if (lowerMessage.includes('ghana card')) {
      code = VERIFICATION_ERROR_CODES.INVALID_GHANA_CARD_FORMAT;
    }
    message = getErrorMessage(code);
  }
  
  return {
    code,
    message,
    details,
    niaCode,
    originalError: error.message,
    status: status || 500
  };
}
